import React, { useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  SurveyAnsweringForm,
  type SurveyAnswers,
  type SurveyAnswerValue,
  type SurveyFormBuilderElement,
  type SurveyFormSubmitResult,
  type SurveySubmitAnswers,
} from '@factorialco/f0-react'
import {
  CreatePurchaseRequestDocument,
  type FormEditorFragment,
  MyPurchaseRequestsDataCollectionDocument,
  type ProcurementTypeForRequestFragment,
  type ProcurementTypeItemFragment,
  PurchaseRequestDetailDocument,
  UpdateAndSetToPendingPurchaseRequestDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import {
  FromFragmentToQuestionnaire,
  FromQuestionnaireToForm,
  type FromQuestionnaireToFormOptions,
} from 'modules/forms/components/newFormEditor/helpers'
import { usePurchaseRequestDatasets } from 'modules/procurement/components/purchaseRequests/PurchaseRequestForm/datasets'

export interface ExistingPurchaseRequest {
  id: string
  description: string | null
  cost: { cents: number; currency: string } | null
  url: string | null | undefined
  deadline: string | null | undefined
  additionalInformation: string | null | undefined
  vendor: { id: string } | null
  formsMembership: {
    answers: Array<{
      answer: string
      element: { id: string }
    }> | null
  } | null
}

interface Props {
  procurementType: ProcurementTypeItemFragment | ProcurementTypeForRequestFragment | null
  isOpen: boolean
  onClose: () => void
  existingPurchaseRequest?: ExistingPurchaseRequest | null
}

const QUESTIONNAIRE_OPTIONS: FromQuestionnaireToFormOptions = {
  mapDropdownSingleTo: 'dropdown-single',
  dropdownSingleShowSearchBox: true,
  mapDropdownMultiTo: 'dropdown-multi',
}

function formToSurveyElements(form: FormEditorFragment): SurveyFormBuilderElement[] {
  const questionnaire = FromFragmentToQuestionnaire(form)
  return FromQuestionnaireToForm(questionnaire.elements, QUESTIONNAIRE_OPTIONS)
}

interface PurchaseRequestFields {
  description: string
  cost: number
  currency: string
  url: string | undefined
  deadline: string | undefined
  additionalInformation: string | undefined
  vendorId: string | undefined
}

type FormAnswer = {
  answer: string
  elementId: string
  elementOptionIds: string[] | null
}

interface ProcessedAnswers {
  fields: PurchaseRequestFields
  allAnswers: FormAnswer[]
  files: File[]
}

const PURCHASE_REQUEST_FIELD_IDENTIFIERS = new Set([
  'request_title',
  'description',
  'cost',
  'currency',
  'vendor',
  'url',
  'due_date',
])

// Fallback mapping for legacy forms created before field_identifier was added.
// Matches elements by relative order + type within the first section, mirroring
// the backend's MigrateLegacyPurchaseRequestForms backfill expected layout.
const LEGACY_FIELD_SEQUENCE: Array<{ types: string[]; identifier: string }> = [
  { types: ['text'], identifier: 'request_title' },
  { types: ['number'], identifier: 'cost' },
  { types: ['dropdownsingle', 'select', 'radio_single'], identifier: 'currency' },
  { types: ['dropdownsingle', 'select', 'radio_single'], identifier: 'vendor' },
  { types: ['url'], identifier: 'url' },
  { types: ['date'], identifier: 'due_date' },
  { types: ['text'], identifier: 'description' },
]

function buildFieldIdentifierMap(form: FormEditorFragment): Map<string, string> {
  const map = new Map<string, string>()

  // First pass: collect explicit fieldIdentifiers
  for (const el of form.elements) {
    if (el.params.fieldIdentifier) {
      map.set(el.id, el.params.fieldIdentifier)
    }
  }

  // If all required identifiers are present, no fallback needed
  const presentRequiredIdentifiers = new Set(
    [...map.values()].filter((v) => PURCHASE_REQUEST_FIELD_IDENTIFIERS.has(v))
  )
  if (presentRequiredIdentifiers.size === PURCHASE_REQUEST_FIELD_IDENTIFIERS.size) return map

  // Fallback: for elements still missing identifiers, match by sorted order + type
  // within the first section. Handles legacy forms and partially migrated forms.
  const sectionElement = form.elements.find((el) => el.type === 'section')
  if (!sectionElement) return map

  const sectionElements = form.elements
    .filter((el) => el.sectionId === sectionElement.id)
    .sort((a, b) => a.position - b.position)

  const assignedIdentifiers = new Set(map.values())
  for (let i = 0; i < Math.min(sectionElements.length, LEGACY_FIELD_SEQUENCE.length); i++) {
    const el = sectionElements[i]
    const spec = LEGACY_FIELD_SEQUENCE[i]
    if (
      el &&
      spec &&
      !map.has(el.id) &&
      !assignedIdentifiers.has(spec.identifier) &&
      spec.types.includes(el.type)
    ) {
      map.set(el.id, spec.identifier)
      assignedIdentifiers.add(spec.identifier)
    }
  }

  return map
}

function processFormAnswers(
  answers: SurveySubmitAnswers,
  form: FormEditorFragment,
  pendingFiles: Map<string, File>
): ProcessedAnswers {
  const fields: PurchaseRequestFields = {
    description: '',
    cost: 0,
    currency: '',
    url: undefined,
    deadline: undefined,
    additionalInformation: undefined,
    vendorId: undefined,
  }
  const allAnswers: FormAnswer[] = []
  const files: File[] = []

  const identifierMap = buildFieldIdentifierMap(form)

  for (const [elementId, value] of Object.entries(answers)) {
    const element = form.elements.find((el) => el.id === elementId)
    if (!element) continue

    if (element.type === 'file') {
      if (Array.isArray(value)) {
        for (const fileId of value) {
          const file = pendingFiles.get(String(fileId))
          if (file) files.push(file)
        }
      }
      continue
    }

    const identifier = identifierMap.get(elementId)
    if (identifier && PURCHASE_REQUEST_FIELD_IDENTIFIERS.has(identifier)) {
      mapIdentifiedField(fields, identifier, value, element)
    }

    // All non-file answers are sent as form answers so the formsMembership
    // is always created and the Forms tab can display them.
    allAnswers.push({
      answer: formatAnswerValue(value),
      elementId,
      elementOptionIds: extractOptionIds(value, element),
    })
  }

  return { fields, allAnswers, files }
}

function mapIdentifiedField(
  fields: PurchaseRequestFields,
  identifier: string,
  value: SurveySubmitAnswers[string],
  element: FormEditorFragment['elements'][number]
): void {
  switch (identifier) {
    case 'request_title':
      fields.description = String(value ?? '')
      break
    case 'description':
      fields.additionalInformation = String(value ?? '')
      break
    case 'cost':
      fields.cost = Number(value) ? toCents(Number(value)) : 0
      break
    case 'currency': {
      const raw = String(value ?? '')
      const option = element.options.find((opt) => opt.id === raw)
      fields.currency = option ? option.value : raw
      break
    }
    case 'vendor': {
      // Legacy forms return the option ID; resolve to option.value (finance.contact id).
      // Dataset-backed forms return the contact id directly.
      const rawVendor = String(value ?? '')
      const vendorOption = element.options.find((opt) => opt.id === rawVendor)
      fields.vendorId = vendorOption ? vendorOption.value : rawVendor || undefined
      break
    }
    case 'url':
      fields.url = value ? String(value) : undefined
      break
    case 'due_date':
      if (value instanceof Date) {
        fields.deadline = formatLocalDate(value)
      } else if (typeof value === 'string' && value) {
        fields.deadline = value
      }
      break
  }
}

function formatLocalDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatAnswerValue(value: SurveySubmitAnswers[string]): string {
  if (value === undefined || value === null) return ''
  if (value instanceof Date) return formatLocalDate(value)
  if (typeof value === 'boolean') return value ? 'true' : ''
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value)) return value.join(',')
  return String(value)
}

function extractOptionIds(
  value: SurveySubmitAnswers[string],
  element: FormEditorFragment['elements'][number]
): string[] | null {
  // Dataset-backed dropdowns (currency, vendor) have no static options —
  // the selected value is a data key (e.g. "EUR"), not a forms_element_option ID.
  if (element.params.dataset) return null

  if (element.type === 'select' || element.type === 'dropdownsingle') {
    if (typeof value === 'string') return [value]
  }
  if (element.type === 'multiselect' || element.type === 'dropdownmulti') {
    if (Array.isArray(value)) return value.map(String)
  }
  return null
}

function buildEditDefaultValues(
  form: FormEditorFragment,
  existing: ExistingPurchaseRequest
): Partial<SurveyAnswers> {
  const values: Partial<SurveyAnswers> = {}
  const identifierMap = buildFieldIdentifierMap(form)

  for (const element of form.elements) {
    const identifier = identifierMap.get(element.id)

    if (identifier) {
      switch (identifier) {
        case 'request_title':
          if (existing.description) {
            values[element.id] = { type: 'text', value: existing.description }
          }
          break
        case 'description':
          if (existing.additionalInformation) {
            values[element.id] = { type: 'longText', value: existing.additionalInformation }
          }
          break
        case 'cost':
          if (existing.cost) {
            values[element.id] = { type: 'numeric', value: existing.cost.cents / 100 }
          }
          break
        case 'currency':
          if (existing.cost?.currency) {
            values[element.id] = { type: 'dropdown-single', value: existing.cost.currency }
          }
          break
        case 'vendor':
          if (existing.vendor?.id) {
            values[element.id] = { type: 'dropdown-single', value: existing.vendor.id }
          }
          break
        case 'url':
          if (existing.url) {
            values[element.id] = { type: 'link', value: existing.url }
          }
          break
        case 'due_date':
          if (existing.deadline) {
            values[element.id] = {
              type: 'date',
              value: new Date(existing.deadline + 'T00:00:00'),
            }
          }
          break
      }
    } else {
      const answer = existing.formsMembership?.answers?.find((a) => a.element.id === element.id)
      if (answer?.answer) {
        const mapped = mapAnswerToSurveyValue(element.type, answer.answer)
        if (mapped) values[element.id] = mapped
      }
    }
  }

  return values
}

function mapAnswerToSurveyValue(
  elementType: string,
  answer: string
): SurveyAnswerValue | undefined {
  switch (elementType) {
    case 'text':
      return { type: 'text', value: answer }
    case 'longtext':
      return { type: 'longText', value: answer }
    case 'number':
      return { type: 'numeric', value: Number(answer) }
    case 'url':
      return { type: 'link', value: answer }
    case 'date':
      return { type: 'date', value: new Date(answer + 'T00:00:00') }
    case 'checkbox':
      return { type: 'checkbox', value: answer === 'true' || answer === '1' }
    case 'select':
    case 'dropdownsingle':
      return { type: 'dropdown-single', value: answer }
    case 'multiselect':
    case 'dropdownmulti':
      return { type: 'dropdown-multi', value: answer.split(',') }
    default:
      return { type: 'text', value: answer }
  }
}

export const PurchaseRequestForm: React.FC<Props> = ({
  procurementType,
  isOpen,
  onClose,
  existingPurchaseRequest = null,
}) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company, employee } = useCurrentSession()

  const datasets = usePurchaseRequestDatasets(company.id)

  const [createPurchaseRequest] = useMutation(CreatePurchaseRequestDocument, {
    extractorFn: (data) => data.procurementMutations.createProcurementPurchaseRequest,
    refetchQueries: [MyPurchaseRequestsDataCollectionDocument],
  })

  const [updateAndSetToPendingPurchaseRequest] = useMutation(
    UpdateAndSetToPendingPurchaseRequestDocument,
    {
      extractorFn: (data) =>
        data.procurementMutations.updateAndSetToPendingProcurementPurchaseRequest,
      refetchQueries: [MyPurchaseRequestsDataCollectionDocument, PurchaseRequestDetailDocument],
    }
  )

  const isEdit = !!existingPurchaseRequest?.id

  const pendingFilesRef = useRef(new Map<string, File>())

  const handleClose = useCallback(() => {
    pendingFilesRef.current.clear()
    onClose()
  }, [onClose])

  const useUpload = useCallback(
    () => ({
      upload: async (file: File) => {
        const fileId = crypto.randomUUID()
        pendingFilesRef.current.set(fileId, file)
        return { type: 'success' as const, value: fileId }
      },
      progress: 100,
      status: 'success' as const,
    }),
    []
  )

  const id = procurementType?.id
  const form = procurementType?.formsConnection.edges[0]?.node

  const baseElements = useMemo<SurveyFormBuilderElement[]>(() => {
    if (!form || form.elements.length === 0) return []
    return formToSurveyElements(form)
  }, [form])

  const defaultValues = useMemo<Partial<SurveyAnswers> | undefined>(() => {
    if (!form) return undefined

    if (existingPurchaseRequest) {
      return buildEditDefaultValues(form, existingPurchaseRequest)
    }

    const userCurrency = employee.legalEntity?.currency ?? company.currency
    if (!userCurrency) return undefined
    const idMap = buildFieldIdentifierMap(form)
    const currencyElementId = [...idMap.entries()].find(([, v]) => v === 'currency')?.[0]
    if (!currencyElementId) return undefined
    return {
      [currencyElementId]: { type: 'dropdown-single' as const, value: userCurrency },
    }
  }, [form, existingPurchaseRequest, employee.legalEntity?.currency, company.currency])

  const module = useMemo(
    () => ({
      id: 'finance-spending' as const,
      label: i18n.t('sidebar.finance'),
      href: navigation.procurement.fullPath(),
    }),
    [navigation]
  )

  const handleSubmit = useCallback(
    async (answers: SurveySubmitAnswers): Promise<SurveyFormSubmitResult> => {
      if (!form || !id) {
        return { success: false }
      }

      app.setProgress({
        message: isEdit
          ? i18n.t('procurement.purchase_requests.new_request_wizard.notifications.updating')
          : i18n.t('procurement.purchase_requests.new_request_wizard.notifications.creating'),
      })

      const { fields, allAnswers, files } = processFormAnswers(
        answers,
        form,
        pendingFilesRef.current
      )

      const currency = fields.currency || employee.legalEntity?.currency || company.currency || ''

      let handled
      try {
        if (isEdit && existingPurchaseRequest?.id) {
          const result = await updateAndSetToPendingPurchaseRequest({
            variables: {
              id: existingPurchaseRequest.id,
              companyId: company.id,
              description: fields.description,
              cost: { cents: fields.cost, currency },
              date: formatLocalDate(new Date()),
              deadline: fields.deadline,
              url: fields.url,
              additionalInformation: fields.additionalInformation,
              vendorId: fields.vendorId,
              typeId: id,
              files,
              answers: allAnswers,
              formId: form.id,
            },
          })

          handled = handleResult(result, () => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t(
                'procurement.purchase_requests.new_request_wizard.notifications.error_updating'
              ),
            })
          })
        } else {
          const result = await createPurchaseRequest({
            variables: {
              description: fields.description,
              typeId: id,
              amount: fields.cost,
              currency,
              url: fields.url,
              deadline: fields.deadline,
              additionalInformation: fields.additionalInformation,
              date: formatLocalDate(new Date()),
              files,
              companyId: company.id,
              requesterEmployeeId: employee.id,
              vendorId: fields.vendorId,
              answers: allAnswers,
              formId: form.id,
            },
          })

          handled = handleResult(result, () => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t(
                'procurement.purchase_requests.new_request_wizard.notifications.error'
              ),
            })
          })
        }
      } finally {
        app.setProgress(null)
      }

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: isEdit
            ? i18n.t(
                'procurement.purchase_requests.new_request_wizard.notifications.success_updating'
              )
            : i18n.t('procurement.purchase_requests.new_request_wizard.notifications.success'),
        })

        if (!isEdit) {
          track(trackingEvents.procurement.submitNewPurchaseRequest, {
            description: fields.description,
            cost: String(fields.cost),
            currency,
            deadline: fields.deadline ? 'yes' : 'no',

            vendorNew: 'no',
            vendorTaxId: '',
            attachments: files.length > 0 ? 'yes' : 'no',
          })
        }

        onClose()
        if (!isEdit) {
          navigate(navigation.myPurchases.fullPath())
        }
        return { success: true }
      }

      return { success: false }
    },
    [
      form,
      id,
      isEdit,
      existingPurchaseRequest,
      createPurchaseRequest,
      updateAndSetToPendingPurchaseRequest,
      company.id,
      company.currency,
      employee.id,
      employee.legalEntity?.currency,
      onClose,
      navigate,
      navigation,
    ]
  )

  if (!procurementType) return null

  return (
    <SurveyAnsweringForm
      elements={baseElements}
      datasets={datasets}
      mode="all-questions"
      title={
        isEdit
          ? i18n.t('procurement.purchase_requests.new_request_wizard.title_edit')
          : i18n.t('procurement.purchase_requests.new_request_wizard.title')
      }
      description={procurementType.description ?? undefined}
      module={module}
      isOpen={isOpen}
      onClose={handleClose}
      allowToChangeFullscreen
      position="fullscreen"
      errorTriggerMode="on-submit"
      loading={false}
      defaultValues={defaultValues}
      useUpload={useUpload}
      onSubmit={handleSubmit}
      labels={{
        empty: {
          title: i18n.t('f0.survey_answering_form.labels.empty.title'),
          description: i18n.t('f0.survey_answering_form.labels.empty.description'),
          emoji: '📝',
        },
      }}
    />
  )
}
